<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { format } from 'date-fns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppCampaignLimitsWidget from 'dashboard/components-next/Campaigns/WhatsAppCampaignLimitsWidget.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const isProcessing = ref(false);

const campaignId = computed(() => Number(route.params.campaignId));

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const timeToParse = typeof dateString === 'number' ? dateString * 1000 : dateString;
    return format(new Date(timeToParse), 'MMM d, yyyy h:mm a');
  } catch (e) {
    return 'Invalid Date';
  }
};

const campaign = computed(() => {
  return store.getters['campaigns/getAllCampaigns'].find(
    c => c.id === campaignId.value
  );
});

onMounted(() => {
  if (!store.getters['campaigns/getAllCampaigns'].length) {
    store.dispatch('campaigns/get');
  }
  fetchMetrics();
  
  startPolling();
});

let pollingInterval = null;

const startPolling = () => {
  if (pollingInterval) clearInterval(pollingInterval);
  pollingInterval = setInterval(() => {
    if (campaign.value && !['completed', 'failed'].includes(campaign.value.campaign_status)) {
      store.dispatch('campaigns/get');
    }
    // Siempre refrescar métricas mientras esté en la vista,
    // ya que los recibos de WhatsApp (entregado/leído) llegan asíncronamente
    // incluso después de que la campaña marque "completed".
    if (campaign.value) {
      fetchMetrics();
    }
  }, 10000); // 10 seconds
};

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
});

const limitsState = ref({
  isLoading: false,
  data: { plan_available: 0, meta_daily_limit: 0, sends_today: 0, remaining_today: 0 }
});

const metricsState = ref({
  isLoading: false,
  data: { total: 0, pending: 0, processing: 0, delivered: 0, read: 0, failed: 0 }
});

const fetchMetrics = async () => {
  metricsState.value.isLoading = true;
  try {
    const data = await store.dispatch('campaigns/fetchMetrics', campaignId.value);
    metricsState.value.data = data || { total: 0, pending: 0, processing: 0, delivered: 0, read: 0, failed: 0 };
  } catch (error) {
    // ignore
  } finally {
    metricsState.value.isLoading = false;
  }
};

const fetchLimits = async (inboxId) => {
  if (!inboxId) return;
  limitsState.value.isLoading = true;
  try {
    const data = await store.dispatch('campaigns/fetchLimits', inboxId);
    limitsState.value.data = data || { plan_available: 0, meta_daily_limit: 0, sends_today: 0, remaining_today: 0 };
  } catch (error) {
    // ignore
  } finally {
    limitsState.value.isLoading = false;
  }
};

watch(() => campaign.value?.inbox?.id, (newInboxId) => {
  if (newInboxId) fetchLimits(newInboxId);
}, { immediate: true });

const isPaused = computed(() => campaign.value?.campaign_status === 'paused');
const canPause = computed(() => ['scheduled', 'in_progress'].includes(campaign.value?.campaign_status));
const canCancel = computed(() => campaign.value?.campaign_status === 'scheduled');
const isCompleted = computed(() => campaign.value?.campaign_status === 'completed');

const mediaUrl = computed(() => {
  return campaign.value?.template_params?.processed_params?.header?.media_url;
});

const isImageMedia = computed(() => {
  const url = mediaUrl.value;
  if (!url) return false;
  return url.match(/\.(jpeg|jpg|gif|png|webp)$/i) != null;
});

const isVideoMedia = computed(() => {
  const url = mediaUrl.value;
  if (!url) return false;
  return url.match(/\.(mp4|webm|ogg)$/i) != null;
});

const totalDeliveries = computed(() => metricsState.value.data.total || 0);

const handlePause = async () => {
  try {
    isProcessing.value = true;
    await store.dispatch('campaigns/pause', campaignId.value);
    useAlert('Campaña pausada exitosamente.');
  } catch (error) {
    useAlert('Error al pausar la campaña.');
  } finally {
    isProcessing.value = false;
  }
};

const handleResume = async () => {
  try {
    isProcessing.value = true;
    await store.dispatch('campaigns/resume', campaignId.value);
    useAlert('Campaña retomada exitosamente.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Error al retomar la campaña.';
    useAlert(errorMessage);
  } finally {
    isProcessing.value = false;
  }
};

const handleCancel = async () => {
  if (!confirm('¿Estás seguro de que deseas cancelar y eliminar esta campaña?')) return;
  try {
    isProcessing.value = true;
    await store.dispatch('campaigns/delete', campaignId.value);
    useAlert('Campaña cancelada exitosamente.');
    router.back();
  } catch (error) {
    useAlert('Error al cancelar la campaña.');
  } finally {
    isProcessing.value = false;
  }
};

const goBack = () => {
  router.back();
};

const handleExport = async () => {
  isProcessing.value = true;
  try {
    const response = await store.dispatch('campaigns/exportDeliveries', campaignId.value);
    // Create a blob from the response and trigger download
    const blob = new Blob([response], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_${campaignId.value}_deliveries.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    useAlert('Informe exportado exitosamente.');
  } catch (error) {
    useAlert('Error al exportar el informe.');
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto bg-n-background">
    <div v-if="!campaign" class="flex items-center justify-center h-full">
      <Spinner />
    </div>
    <div v-else class="p-8 max-w-5xl mx-auto w-full">
      <div class="flex justify-between items-center mb-8">
        <div class="flex items-center gap-4">
          <Button variant="ghost" color="slate" icon="i-lucide-arrow-left" @click="goBack" />
          <h1 class="text-2xl font-semibold text-n-slate-12">Detalles de Campaña: {{ campaign.title }}</h1>
        </div>
        <div class="flex gap-2">
          <Button
            v-if="canCancel"
            label="Cancelar Campaña"
            color="ruby"
            icon="i-lucide-x"
            :is-loading="isProcessing"
            @click="handleCancel"
          />
          <Button
            v-if="canPause"
            label="Pausar Campaña"
            color="amber"
            icon="i-lucide-pause"
            :is-loading="isProcessing"
            @click="handlePause"
          />
          <Button
            v-if="isPaused"
            label="Retomar Campaña"
            color="teal"
            icon="i-lucide-play"
            :is-loading="isProcessing"
            @click="handleResume"
          />
          <span v-if="isCompleted" class="px-3 py-1 rounded-full bg-n-green-3 text-n-green-11 font-medium text-sm">
            Completada
          </span>
          <Button
            label="Exportar Informe"
            color="slate"
            variant="faded"
            icon="i-lucide-download"
            :is-loading="isProcessing"
            @click="handleExport"
          />
        </div>
      </div>

      <div class="grid grid-cols-3 gap-6 mb-8">
        <div class="col-span-2 grid grid-cols-2 gap-6">
          <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
            <h3 class="text-n-slate-11 text-sm font-medium mb-2">Estado</h3>
            <div class="flex items-center gap-2">
              <p class="text-2xl font-bold text-n-slate-12 capitalize">{{ campaign.campaign_status }}</p>
              <div v-if="isPaused && campaign.trigger_rules?.pause_reason" class="text-xs px-2 py-1 bg-n-amber-3 text-n-amber-11 rounded-md max-w-xs truncate" :title="campaign.trigger_rules.pause_reason">
                {{ campaign.trigger_rules.pause_reason }}
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
            <h3 class="text-n-slate-11 text-sm font-medium mb-2">Total de Mensajes</h3>
            <p class="text-2xl font-bold text-n-slate-12">{{ totalDeliveries }}</p>
          </div>
          <div class="col-span-2 bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
            <h3 class="text-n-slate-11 text-sm font-medium mb-2">Tipo de Bandeja</h3>
            <p class="text-2xl font-bold text-n-slate-12">{{ campaign.inbox?.name || 'N/A' }}</p>
          </div>
          <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
            <h3 class="text-n-slate-11 text-sm font-medium mb-2">Fecha Programada</h3>
            <p class="text-lg font-bold text-n-slate-12">{{ formatDateTime(campaign.scheduled_at) }}</p>
          </div>
          <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
            <h3 class="text-n-slate-11 text-sm font-medium mb-2">Última Actividad</h3>
            <p class="text-lg font-bold text-n-slate-12">{{ formatDateTime(campaign.updated_at) }}</p>
          </div>
        </div>
        <div class="col-span-1">
          <WhatsAppCampaignLimitsWidget
            :is-loading="limitsState.isLoading"
            :plan-available="limitsState.data.plan_available"
            :meta-daily-limit="limitsState.data.meta_daily_limit"
            :sends-today="limitsState.data.sends_today"
            :remaining-today="limitsState.data.remaining_today"
          />
        </div>
      </div>

      <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm mb-8">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-4">Métricas de Envío</h2>
        <div v-if="metricsState.isLoading" class="h-32 flex items-center justify-center">
          <Spinner />
        </div>
        <div v-else class="grid grid-cols-6 gap-4">
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-slate-2 border border-n-slate-3">
            <span class="text-3xl font-bold text-n-slate-11">{{ metricsState.data.pending || 0 }}</span>
            <span class="text-xs font-medium text-n-slate-11 mt-1 uppercase tracking-wider">Pendientes</span>
          </div>
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-blue-2 border border-n-blue-3">
            <span class="text-3xl font-bold text-n-blue-11">{{ metricsState.data.processing || 0 }}</span>
            <span class="text-xs font-medium text-n-blue-11 mt-1 uppercase tracking-wider">Procesando</span>
          </div>
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-indigo-2 border border-n-indigo-3">
            <span class="text-3xl font-bold text-n-indigo-11">{{ metricsState.data.sent || 0 }}</span>
            <span class="text-xs font-medium text-n-indigo-11 mt-1 uppercase tracking-wider">Enviados</span>
          </div>
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-teal-2 border border-n-teal-3">
            <span class="text-3xl font-bold text-n-teal-11">{{ metricsState.data.delivered || 0 }}</span>
            <span class="text-xs font-medium text-n-teal-11 mt-1 uppercase tracking-wider">Entregados</span>
          </div>
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-green-2 border border-n-green-3">
            <span class="text-3xl font-bold text-n-green-11">{{ metricsState.data.read || 0 }}</span>
            <span class="text-xs font-medium text-n-green-11 mt-1 uppercase tracking-wider">Leídos</span>
          </div>
          <div class="flex flex-col items-center justify-center p-4 rounded-lg bg-n-red-2 border border-n-red-3">
            <span class="text-3xl font-bold text-n-red-11">{{ metricsState.data.failed }}</span>
            <span class="text-xs font-medium text-n-red-11 mt-1 uppercase tracking-wider">Fallidos</span>
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-n-solid-2 border border-n-weak p-6 rounded-xl shadow-sm">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-4">Contenido del Mensaje</h2>
        
        <div v-if="mediaUrl" class="mb-4 p-4 border border-n-weak rounded-lg bg-n-alpha-1 flex flex-col gap-2">
          <p class="text-sm font-medium text-n-slate-12">Multimedia Adjunta</p>
          <img v-if="isImageMedia" :src="mediaUrl" alt="Media" class="max-w-xs rounded-md border border-n-weak" />
          <video v-else-if="isVideoMedia" :src="mediaUrl" controls class="max-w-xs rounded-md border border-n-weak"></video>
          <a v-else :href="mediaUrl" target="_blank" rel="noopener noreferrer" class="text-sm text-n-blue-11 hover:underline flex items-center gap-1">
            <span class="i-lucide-external-link w-4 h-4"></span> Ver archivo multimedia adjunto
          </a>
        </div>

        <div class="bg-n-alpha-1 p-4 rounded-lg text-n-slate-11 whitespace-pre-wrap font-mono text-sm">
          {{ campaign.message }}
        </div>
      </div>
    </div>
  </div>
</template>
